import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { useParentAuth } from "@/context/ParentAuthContext";
import { toast } from "sonner";
import { CheckCircle2, Calendar, MapPin, User, Loader2 } from "lucide-react";

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }); } catch { return s; }
}

export default function CheckIn() {
  const { CATEGORY_LABEL } = useCategories();
  const { eventId } = useParams();
  const { parent, loading: authLoading } = useParentAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!parent) {
      // Redirect to login with return path
      navigate(`/parent/login?next=${encodeURIComponent(`/checkin/${eventId}`)}`, { replace: true });
      return;
    }
    setLoading(true);
    api.get(`/parent/checkin/${eventId}`)
      .then((r) => {
        setData(r.data);
        // pre-select all pending kids
        const init = {};
        (r.data.competitors || []).forEach((c) => {
          if (c.status !== "arrived") init[c.id] = true;
        });
        setPicked(init);
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Could not load check-in.");
      })
      .finally(() => setLoading(false));
  }, [parent, authLoading, eventId, navigate]);

  const submit = async () => {
    const ids = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) {
      toast.error("Pick at least one cuber.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/parent/checkin", { event_id: eventId, competitor_ids: ids });
      const names = (data.competitors || []).filter((c) => ids.includes(c.id)).map((c) => c.full_name);
      setDone({ count: res.count, names });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check-in failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FDFFFC] grid place-items-center px-5">
        <div className="text-[#7A7D8C] flex items-center gap-3" data-testid="checkin-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Getting your cubers…
        </div>
      </div>
    );
  }

  if (!parent) return null; // redirecting

  if (done) {
    return <AllSetScreen event={data?.event} names={done.names} />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFFFC] grid place-items-center px-5 py-12">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 p-8 text-center" data-testid="checkin-error">
          <h1 className="font-display font-black text-2xl">Hmm.</h1>
          <p className="mt-2 text-[#7A7D8C]">{error}</p>
          <Link to="/parent" className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#FF9F1C] text-white font-bold text-sm">Go to my dashboard</Link>
        </div>
      </div>
    );
  }

  const ev = data?.event;
  const kids = data?.competitors || [];
  const eligible = kids.filter((c) => c.status !== "arrived");
  const already = kids.filter((c) => c.status === "arrived");

  return (
    <div className="min-h-screen bg-[#FDFFFC] px-5 py-10 md:py-16">
      <div className="max-w-lg mx-auto">
        <div className="text-center">
          <span className="text-[11px] uppercase tracking-widest font-black text-[#FF9F1C]">Event Check-In</span>
          <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="checkin-event-title">{ev?.title}</h1>
          <div className="mt-3 flex flex-wrap justify-center gap-4 text-xs text-[#7A7D8C]">
            {ev?.start_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(ev.start_date)}</span>}
            {ev?.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {ev.location}</span>}
          </div>
        </div>

        <div className="mt-8 bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-[0_10px_40px_rgb(0,0,0,0.05)]">
          <p className="text-sm text-[#7A7D8C]">Signed in as <b className="text-[#1E1E24]">{parent.email}</b></p>

          {kids.length === 0 ? (
            <div className="mt-6 rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center" data-testid="checkin-no-kids">
              <p className="font-semibold">No cubers registered under your account for this event.</p>
              <p className="mt-2 text-sm text-[#7A7D8C]">If your child registered under a different email, ask an organizer at the door to check them in manually.</p>
              <Link to="/parent" className="mt-5 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#FF9F1C] text-white font-bold text-sm">My dashboard</Link>
            </div>
          ) : eligible.length === 0 ? (
            <div className="mt-6 rounded-2xl bg-[#2EC4B6]/10 border border-[#2EC4B6]/30 p-6 text-center" data-testid="checkin-all-arrived">
              <CheckCircle2 className="w-8 h-8 text-[#2EC4B6] mx-auto" />
              <p className="mt-3 font-display font-black text-xl">All checked in!</p>
              <p className="mt-1 text-sm text-[#7A7D8C]">{already.map((c) => c.full_name).join(", ")} — enjoy the event.</p>
            </div>
          ) : (
            <>
              <h2 className="mt-6 font-display font-black text-lg">
                {eligible.length === 1 ? "Confirm you're here" : "Which of your cubers are competing?"}
              </h2>
              {eligible.length > 1 && (
                <p className="text-xs text-[#7A7D8C] mt-1">Tap any that are with you today.</p>
              )}

              <ul className="mt-4 space-y-2" data-testid="checkin-kid-list">
                {eligible.map((c) => {
                  const on = !!picked[c.id];
                  return (
                    <li key={c.id}>
                      <button
                        onClick={() => setPicked((p) => ({ ...p, [c.id]: !p[c.id] }))}
                        data-testid={`checkin-kid-${c.id}`}
                        className={`w-full flex items-center gap-4 rounded-2xl border-2 p-4 text-left transition-all ${on ? "border-[#FF9F1C] bg-[#FFBF69]/10" : "border-slate-200 hover:border-[#FFBF69]"}`}
                      >
                        <span className={`w-6 h-6 rounded-full border-2 grid place-items-center shrink-0 ${on ? "bg-[#FF9F1C] border-[#FF9F1C]" : "border-slate-300"}`}>
                          {on && <CheckCircle2 className="w-4 h-4 text-white" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block font-semibold truncate">{c.full_name}</span>
                          <span className="block text-xs text-[#7A7D8C]">
                            {c.assigned_category ? CATEGORY_LABEL[c.assigned_category] : "Category to be assigned"}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {already.length > 0 && (
                <p className="mt-4 text-xs text-[#7A7D8C]" data-testid="checkin-already-arrived">
                  Already checked in: {already.map((c) => c.full_name).join(", ")}
                </p>
              )}

              <button
                onClick={submit}
                disabled={submitting}
                data-testid="checkin-submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_12px_30px_rgb(255,159,28,0.35)] disabled:opacity-60"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking you in…</> : "Check in"}
              </button>
            </>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link to="/parent" className="text-sm text-[#7A7D8C] hover:text-[#FF9F1C]"><User className="w-3.5 h-3.5 inline mr-1" /> Parent dashboard</Link>
        </div>
      </div>
    </div>
  );
}

function AllSetScreen({ event, names }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFBF69]/30 via-[#FDFFFC] to-[#FDE74C]/30 grid place-items-center px-5 py-16" data-testid="checkin-allset">
      <div className="max-w-md w-full text-center">
        <div className="w-24 h-24 rounded-full bg-[#2EC4B6] text-white grid place-items-center mx-auto shadow-[0_20px_60px_rgb(46,196,182,0.35)]">
          <CheckCircle2 className="w-14 h-14" />
        </div>
        <h1 className="mt-8 font-display font-black text-5xl tracking-tight">You&apos;re all set!</h1>
        <p className="mt-4 text-lg text-[#7A7D8C]">
          {names.length === 1
            ? <><b className="text-[#1E1E24]">{names[0]}</b> is checked in for {event?.title}.</>
            : <><b className="text-[#1E1E24]">{names.join(", ")}</b> are checked in for {event?.title}.</>}
        </p>
        <p className="mt-2 text-sm text-[#7A7D8C]">Grab a seat — a judge will call you when it&apos;s your turn.</p>

        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/parent" data-testid="checkin-done-dashboard" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_12px_30px_rgb(255,159,28,0.35)]">
            My dashboard
          </Link>
          <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-200 font-bold hover:border-[#FF9F1C] hover:text-[#FF9F1C]">
            Home
          </Link>
        </div>
      </div>
    </div>
  );
}
